import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { MOTION, PULSE_DOT } from "../../../theme/tokens";

type Props = {
	/** Palette path (`primary.main`) or a colour already resolved from the theme. Default `primary.main`. */
	color?: string;
	/** Dot diameter in px. Default `PULSE_DOT.SIZE`. */
	size?: number;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * An ambient "still working on it" indicator: a steady dot with one slow ring swelling out of it. For a wait with
 * no end the user can see — a screen that keeps checking, a sync still running — where `JumpingDots` (a short,
 * busy wait) would bounce for minutes. Decorative: the text beside it says what is happening, so it is
 * `aria-hidden`. Under reduced motion the ring is dropped and the dot stays.
 */
export const PulseDot: FC<Props> = ({ color = "primary.main", size = PULSE_DOT.SIZE, dataTestId, sx }) => (
	<Box
		component="span"
		aria-hidden
		data-testid={dataTestId}
		sx={[
			{
				position: "relative",
				display: "inline-block",
				flexShrink: 0,
				width: size,
				height: size,
				borderRadius: "50%",
				bgcolor: color,
				"@keyframes pulseDotRing": {
					from: { transform: "scale(1)", opacity: PULSE_DOT.RING_START_OPACITY },
					to: { transform: `scale(${PULSE_DOT.RING_SCALE})`, opacity: 0 },
				},
				"&::after": {
					content: '""',
					position: "absolute",
					inset: 0,
					borderRadius: "inherit",
					bgcolor: color,
					animation: `pulseDotRing ${PULSE_DOT.PERIOD_MS}ms ${MOTION.EASE_OUT} infinite`,
				},
				"@media (prefers-reduced-motion: reduce)": { "&::after": { animation: "none", opacity: 0 } },
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	/>
);
